import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './JqCommissionGuide.css';
import { money } from '../utils/money';

const API_BASE = '/api';
const rm = (n) => money(Number(n) || 0);
const monthLabel = (m) => new Date(m + '-01').toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
const fmtDate = (s) => new Date(s).toLocaleDateString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', year: 'numeric', month: 'short', day: 'numeric' });

function JqCommissionGuide() {
  const { isSuperadmin } = useAuth();
  const [year, setYear] = useState('2026');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/jq/commission?year=${year}`, { headers: headers() });
      setData(res.data);
    } catch (e) { setError('Failed to load commission data'); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const release = async (id) => {
    const val = window.prompt('Enter the FINAL actual contract value for this event:');
    if (val == null) return;
    try {
      await axios.put(`${API_BASE}/jq-bookings/${id}/deliver`, { actual_final_value: val }, { headers: headers() });
      await load();
    } catch (e) { setError(e.response?.data?.error || 'Release failed'); }
  };

  if (!data) return <div className="jqc-page"><h1>Commission Guide — JQ</h1>{error && <div className="jqc-error">{error}</div>}<p className="jqc-muted">Loading…</p></div>;

  const tierPct = (r) => (parseFloat(r) * 100).toFixed(2).replace(/\.00$/, '.0') + ' %';
  const tierRange = (t) => {
    const min = parseFloat(t.revenue_min), max = t.revenue_max ? parseFloat(t.revenue_max) : null;
    if (min === 0 && max) return `< ${rm(Math.ceil(max))}`;
    if (!max) return `≥ ${rm(min)}`;
    return `${rm(min)} – ${rm(max)}`;
  };

  return (
    <div className="jqc-page">
      <div className="jqc-head">
        <h1>Commission Guide — JQ</h1>
        <label className="jqc-muted">Year:{' '}
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </label>
      </div>
      {error && <div className="jqc-error">{error}</div>}

      <div className="jqc-kpis jqc-kpis-4">
        <div className="jqc-card"><div className="label">70% Captured (YTD)</div><div className="value">{rm(data.ytd.captured70)}</div></div>
        <div className="jqc-card"><div className="label">30% Settled (YTD)</div><div className="value">{rm(data.ytd.settled30)}</div></div>
        <div className="jqc-card"><div className="label">Total Commission (YTD)</div><div className="value">{rm(data.ytd.total)}</div></div>
        <div className="jqc-card"><div className="label">Total Earned (incl. basic)</div><div className="value">{rm(data.ytd.totalEarned)}</div></div>
      </div>

      <div className="jqc-section-title">Monthly Commission (May–Dec {data.year})</div>
      <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
        <thead><tr><th>Month</th><th>Floor Target</th><th>70% Captured</th><th>30% Settled</th><th>Commission</th><th>Basic Salary</th><th>Total Earned</th></tr></thead>
        <tbody>
          {data.months.map(m => (
            <tr key={m.month}>
              <td>{monthLabel(m.month)}</td>
              <td>{m.floor ? rm(m.floor) : '—'}</td>
              <td>{rm(m.captured70)}</td>
              <td>{rm(m.settled30)}</td>
              <td>{rm(m.total)}</td>
              <td>{m.basicSalary != null ? rm(m.basicSalary) : '—'}</td>
              <td>{m.totalEarned != null ? rm(m.totalEarned) : '—'}</td>
            </tr>
          ))}
          <tr className="total">
            <td>TOTAL</td><td></td>
            <td>{rm(data.ytd.captured70)}</td><td>{rm(data.ytd.settled30)}</td><td>{rm(data.ytd.total)}</td>
            <td>{rm(data.ytd.basicSalary)}</td><td>{rm(data.ytd.totalEarned)}</td>
          </tr>
        </tbody>
      </table></div>
      <div className="jqc-note">70% = paid the month a booking is captured, on the projected contract value. 30% = paid on the event day (may be a later month), on the final actual number — net of clawback if the final is lower than forecast. Tier rate is locked at booking. Total Earned = commission + basic salary.</div>

      <div className="jqc-section-title">Pending Settlements — 30% balance due (event date passed)</div>
      <div className="jqc-pending">
        <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table" style={{ marginBottom: 0 }}>
          <thead><tr><th>Event Date</th><th>Invoice</th><th>Venue</th><th>Forecast Value</th><th>70% Paid</th><th>Locked Rate</th><th></th></tr></thead>
          <tbody>
            {data.pending.length ? data.pending.map(p => (
              <tr key={p.id}>
                <td>{fmtDate(p.event_date)}</td>
                <td>{p.invoice_number}</td>
                <td>{p.venue}</td>
                <td>{rm(p.forecast_contract_value)}</td>
                <td>{rm(p.paid_70_percent)}</td>
                <td>{(parseFloat(p.forecast_tier_rate) || 0).toFixed(2)}%</td>
                <td>{isSuperadmin
                  ? <button className="jqc-btn" onClick={() => release(p.id)}>Release 30%</button>
                  : <span className="jqc-muted">awaiting admin release</span>}</td>
              </tr>
            )) : <tr><td colSpan={7} className="jqc-muted">No balances due. Pending bookings appear here once their event date arrives.</td></tr>}
          </tbody>
        </table></div>
      </div>

      <div className="jqc-ref">
        <div>
          <div className="jqc-section-title">Tier Rate Guide</div>
          <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
            <thead><tr><th>Cumulative Revenue</th><th>Rate</th></tr></thead>
            <tbody>{data.tiers.map(t => (
              <tr key={t.tier_level}><td>{tierRange(t)}</td><td>{tierPct(t.rate)}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
        <div>
          <div className="jqc-section-title">Monthly Floor Targets</div>
          <div className="report-table-scroll" role="region" aria-label="Report table — scroll to view all columns" tabIndex={0}><table className="jqc-table">
            <thead><tr><th>Month</th><th>Floor</th></tr></thead>
            <tbody>{data.months.map(m => (
              <tr key={m.month}><td>{monthLabel(m.month)}</td><td>{m.floor ? rm(m.floor) : '—'}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}

export default JqCommissionGuide;
